import React, { useState, useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { startOfDay, endOfDay, startOfWeek, endOfWeek, subWeeks, startOfMonth, endOfMonth, subMonths, startOfYear, subDays } from 'date-fns';
import { Download, ArrowUp, ArrowDown } from 'lucide-react';
import { CSVLink } from 'react-csv';
import { neutralizeCsvRows } from '../utils/csvExport'; // s9csv-4e1b7c28

// Helper to format date for input[type=date]
const toDateInput = (date) => date.toISOString().split('T')[0];

const CancellationReport = ({ currentUserProfile, employees, openJobEditorModal }) => {
    const [canceledEvents, setCanceledEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterEmployeeId, setFilterEmployeeId] = useState('All');
    const [sortConfig, setSortConfig] = useState({ key: 'updatedAt', direction: 'descending' });

    const [dateRangeOption, setDateRangeOption] = useState('Last 3 Months');
    const [customStartDate, setCustomStartDate] = useState(toDateInput(subMonths(new Date(), 3)));
    const [customEndDate, setCustomEndDate] = useState(toDateInput(new Date()));

    const dateRange = useMemo(() => {
        const now = new Date();
        switch (dateRangeOption) {
            case 'All Time': return { start: null, end: null };
            case 'Today': return { start: startOfDay(now), end: endOfDay(now) };
            case 'Yesterday':
                const yesterday = subDays(now, 1);
                return { start: startOfDay(yesterday), end: endOfDay(yesterday) };
            case 'This Week': return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) };
            case 'Last Week':
                const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
                return { start: lastWeekStart, end: endOfWeek(lastWeekStart, { weekStartsOn: 1 }) };
            case 'This Month': return { start: startOfMonth(now), end: endOfMonth(now) };
            case 'Last Month':
                const lastMonthStart = startOfMonth(subMonths(now, 1));
                return { start: lastMonthStart, end: endOfMonth(lastMonthStart) };
            case 'Last 3 Months': return { start: startOfDay(subMonths(now, 3)), end: endOfDay(now) };
            case 'Last 6 Months': return { start: startOfDay(subMonths(now, 6)), end: endOfDay(now) };
            case 'Last 12 Months': return { start: startOfDay(subMonths(now, 12)), end: endOfDay(now) };
            case 'YTD': return { start: startOfYear(now), end: endOfDay(now) };
            case 'Custom':
                const start = new Date(`${customStartDate}T00:00:00`);
                const end = new Date(`${customEndDate}T00:00:00`);
                return { start: startOfDay(start), end: endOfDay(end) };
            default: return { start: startOfDay(subMonths(now, 3)), end: endOfDay(now) };
        }
    }, [dateRangeOption, customStartDate, customEndDate]);

    useEffect(() => {
        if (!currentUserProfile?.organizationId || (currentUserProfile.role !== 'Admin' && currentUserProfile.role !== 'Manager')) {
            setLoading(false);
            return;
        }
        setLoading(true);
        const orgId = currentUserProfile.organizationId;
        
        let eventsQuery;
        const baseQuery = collection(db, `organizations/${orgId}/jobs`);

        if (dateRange.start && dateRange.end) {
            // Query with date range
            eventsQuery = query(baseQuery,
                where('status', '==', 'Canceled'),
                where('updatedAt', '>=', dateRange.start),
                where('updatedAt', '<=', dateRange.end),
                orderBy('updatedAt', 'desc')
            );
        } else {
            // Query for "All Time" (no date constraints)
            eventsQuery = query(baseQuery,
                where('status', '==', 'Canceled'),
                orderBy('updatedAt', 'desc')
            );
        }
        
        const unsubscribe = onSnapshot(eventsQuery, (snapshot) => {
            const eventsData = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })); // s6costing-docid: doc id wins over a stale stored `id` field
            setCanceledEvents(eventsData);
            setLoading(false);
        }, (error) => {
            console.error("Firestore Query Error:", error);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [currentUserProfile, dateRange]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return <span className="inline-block w-4"></span>;
        return sortConfig.direction === 'ascending' ? <ArrowUp className="inline-block ml-1 h-4 w-4" /> : <ArrowDown className="inline-block ml-1 h-4 w-4" />;
    };

    const sortedAndFilteredEvents = useMemo(() => {
        let filtered = [...canceledEvents];
        if (filterEmployeeId !== 'All') {
            filtered = filtered.filter(event => event.assignedTo?.includes(filterEmployeeId));
        }
        filtered.sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];
            if (sortConfig.key === 'startAt' || sortConfig.key === 'updatedAt') {
                aValue = aValue?.toDate();
                bValue = bValue?.toDate();
            }
            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });
        return filtered;
    }, [canceledEvents, filterEmployeeId, sortConfig]);

    const getEmployeeNames = (assigneeIds) => {
        if (!assigneeIds || !employees) return 'Unassigned';
        return assigneeIds.map(id => employees.find(e => e.id === id)?.name || 'Unknown').join(', ');
    };

    const csvHeaders = [
        { label: "Event Title", key: "title" }, { label: "Customer", key: "customer" }, { label: "Event Date", key: "eventDate" }, { label: "Date Canceled", key: "dateCanceled" }, { label: "Assigned To", key: "assignedTo" }, { label: "Cancellation Reason", key: "reason" }, { label: "Cancellation Notes", key: "details" },
    ];

    const csvData = useMemo(() => {
        return sortedAndFilteredEvents.map(event => ({
            title: event.title, customer: event.clientName, eventDate: event.startAt?.toDate().toLocaleString(), dateCanceled: event.updatedAt?.toDate().toLocaleString(), assignedTo: getEmployeeNames(event.assignedTo), reason: event.cancellationReason, details: event.cancellationDetails || '',
        }));
    }, [sortedAndFilteredEvents, getEmployeeNames]);

    if (currentUserProfile?.role !== 'Admin' && currentUserProfile?.role !== 'Manager') {
        return <Navigate to="/home" replace />;
    }

    return (
        <div className="p-6 md:p-10">
            <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 mb-2">Cancellation Report</h1>
                    <p className="text-gray-500">A log of all canceled events within the organization.</p>
                </div>
                <CSVLink
                    data={neutralizeCsvRows(csvData)}
                    headers={csvHeaders}
                    filename={`cancellation-report-${new Date().toISOString().split('T')[0]}.csv`}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-semibold"
                    style={sortedAndFilteredEvents.length === 0 ? { pointerEvents: 'none', opacity: 0.5 } : {}}
                >
                    <Download size={16} />
                    Download CSV
                </CSVLink>
            </div>

            <div className="flex flex-wrap items-end gap-4 p-4 border rounded-lg bg-gray-50 mb-6">
                <div className="flex-grow min-w-[200px]">
                    <label htmlFor="employee-filter" className="block text-sm font-medium text-gray-700">Filter by Employee</label>
                    <select id="employee-filter" value={filterEmployeeId} onChange={(e) => setFilterEmployeeId(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md">
                        <option value="All">All Employees</option>
                        {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                    </select>
                </div>
                <div className="flex-grow min-w-[200px]">
                    <label htmlFor="date-range-filter" className="block text-sm font-medium text-gray-700">Date Range</label>
                    <select id="date-range-filter" value={dateRangeOption} onChange={(e) => setDateRangeOption(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md">
                        <option>All Time</option>
                        <option>Today</option>
                        <option>Yesterday</option>
                        <option>This Week</option>
                        <option>Last Week</option>
                        <option>This Month</option>
                        <option>Last Month</option>
                        <option>Last 3 Months</option>
                        <option>Last 6 Months</option>
                        <option>Last 12 Months</option>
                        <option>YTD</option>
                        <option>Custom</option>
                    </select>
                </div>
                {dateRangeOption === 'Custom' && (
                    <>
                        <div className="flex-grow min-w-[150px]">
                            <label htmlFor="start-date" className="block text-sm font-medium text-gray-700">Start Date</label>
                            <input type="date" id="start-date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} className="mt-1 block w-full pl-3 pr-2 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" />
                        </div>
                        <div className="flex-grow min-w-[150px]">
                            <label htmlFor="end-date" className="block text-sm font-medium text-gray-700">End Date</label>
                            <input type="date" id="end-date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} className="mt-1 block w-full pl-3 pr-2 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" />
                        </div>
                    </>
                )}
            </div>

            <div className="bg-white shadow-sm rounded-lg overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-500">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 cursor-pointer" onClick={() => requestSort('title')}><div className="flex items-center">Event / Customer {getSortIndicator('title')}</div></th>
                            <th className="px-6 py-3 cursor-pointer" onClick={() => requestSort('startAt')}><div className="flex items-center">Event Date {getSortIndicator('startAt')}</div></th>
                            <th className="px-6 py-3 cursor-pointer" onClick={() => requestSort('updatedAt')}><div className="flex items-center">Date Canceled {getSortIndicator('updatedAt')}</div></th>
                            <th className="px-6 py-3">Assigned To</th>
                            <th className="px-6 py-3">Reason</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="5" className="text-center py-10">Loading report...</td></tr>
                        ) : sortedAndFilteredEvents.length > 0 ? sortedAndFilteredEvents.map(event => (
                            <tr key={event.id} className="bg-white border-b hover:bg-gray-50 cursor-pointer" onClick={() => openJobEditorModal(event)}>
                                <td className="px-6 py-4"><p className="font-medium text-gray-900">{event.title}</p><p className="text-gray-500">{event.clientName}</p></td>
                                <td className="px-6 py-4">{event.startAt?.toDate().toLocaleDateString()}</td>
                                <td className="px-6 py-4">{event.updatedAt?.toDate().toLocaleDateString()}</td>
                                <td className="px-6 py-4">{getEmployeeNames(event.assignedTo)}</td>
                                <td className="px-6 py-4"><p className="font-medium text-gray-800">{event.cancellationReason}</p><p className="text-xs text-gray-500">{event.cancellationDetails}</p></td>
                            </tr>
                        )) : (
                            <tr><td colSpan="5" className="text-center py-10 text-gray-500">No canceled events found for the selected period.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CancellationReport;